import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Table from "../../../components/common/Table";
import { PathName } from "../../../utils/routes";
import {
  getFormatedDate,
  calculateTimeDifferenceInDays,
} from "../../../utils/utilities";
import { useDispatch, useSelector } from "react-redux";
import { listCandidates } from "../../../redux/thunks/Candidate";
import Input from "../../../components/sub-component/Input";
import { ReactComponent as Search } from "../../../assets/icons/Views/candidate/assessments/search.svg";

export default function CandidateListingView() {
  const candidateTableHeader = [
    {
      id: "name",
      name: "Name",
      type: "string",
      disableSorting: true,
      screens: ["web", "tab", "mini_tab", "mob"],
    },
    {
      id: "testTaken",
      name: "Test Taken",
      type: "number",
      disableSorting: true,
      backendSorting: true,
      screens: ["web", "tab", "mini_tab", "mob"],
    },
    {
      id: "email",
      name: "Email",
      type: "string",
      disableSorting: true,
      screens: ["web", "mini_tab", "tab"],
    },
    {
      id: "invitedOn",
      name: "Invited on",
      type: "string",
      disableSorting: true,
      backendSorting: true,
      screens: ["web"],
    },
    {
      id: "lastActivity",
      name: "Last Activity",
      type: "string",
      disableSorting: true,
      backendSorting: true,
      screens: ["web", "tab", "mini_tab", "mob"],
    },
  ];

  const customSorting = {
    testTaken: (a, b) => {
      return a - b;
    },
    invitedOn: (a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB;
    },
    lastActivity: (a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB;
    },
  };

  const searchColumns = [
    "name",
    "testTaken",
    "email",
    "invitedOn",
    "lastActivity",
  ];
  const [customSearch, setCustomSearch] = useState();
  const [loading, setLoading] = useState(true);
  const [highlightActiveRow, setHighlightActiveRow] = useState(false);
  const [testData, setTestData] = useState([]);
  const candidate = useSelector((state) => state.CandidateReducer);
  const auth = useSelector((state) => state.authReducer);
  const subscription = useSelector((state) => state.subscriptionReducer);
  const expireAt =
    subscription?.user_subscription.length > 0
      ? subscription?.user_subscription[0]?.expires_at
      : null;
  const daysLeft = calculateTimeDifferenceInDays(expireAt);
  const openLeftDaysDisclaimer = sessionStorage.getItem(
    "openLeftDaysDisclaimer",
  );

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = new URLSearchParams(location.search);
  const selectedSearch = params.get("search") || "";
  const selectedSortColumn = params.get("sortColumn");
  const selectedSortOrder = (params.get("sortOrder") || "").toUpperCase();
  const selectedPage = parseInt(params.get("page"), 10);
  const initialPage =
    !Number.isNaN(selectedPage) && selectedPage > 0 ? selectedPage : 1;

  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem("assessmentsRowsPerPage");
    return saved ? Math.min(parseInt(saved, 10), 100) : 10;
  });
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [selectedsortOption, setSelectedsortOption] = useState({
    column: selectedSortColumn || "lastActivity",
    order:
      selectedSortColumn && ["ASC", "DESC"].includes(selectedSortOrder)
        ? selectedSortOrder
        : "DESC",
    clickCount: 0,
  });

  const selectedSortBy = {
    testTaken: "total_assessments",
    invitedOn: "invited_on",
    lastActivity: "last_activity",
  }[selectedsortOption.column];

  useMemo(() => {
    if (!candidate?.candidates) return [];
    const _data = candidate?.candidates?.map((candidate_item, index) => {
      return {
        name: candidate_item?.first_name + " " + candidate_item?.last_name,
        testTaken: candidate_item?.total_assessments,
        email: candidate_item?.email,
        invitedOn: getFormatedDate(candidate_item?.invited_on, true),
        lastActivity: getFormatedDate(candidate_item?.last_activity, true),
        ...candidate_item,
      };
    });
    setTestData(_data ? [..._data] : []);
    setLoading(false);
  }, [candidate?.candidates]);

  const setQueryParam = (key, value) => {
    const params = new URLSearchParams(location.search);
    if (value || value === 0) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get("page"), 10);
    const pageNumber =
      !Number.isNaN(pageParam) && pageParam > 0 ? pageParam : 1;
    if (pageNumber !== currentPage) {
      setCurrentPage(pageNumber);
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPage = parseInt(params.get("page"), 10);
    const urlPageNumber = !Number.isNaN(urlPage) && urlPage > 0 ? urlPage : 1;

    if (currentPage !== urlPageNumber) {
      setQueryParam("page", currentPage);
      return;
    }

    if (!params.has("page")) {
      setQueryParam("page", currentPage);
    }
  }, [currentPage, location.pathname, location.search]);

  useEffect(() => {
    dispatch(
      listCandidates({
        page: currentPage,
        limit: itemsPerPage,
        search: selectedSearch,
        sort_by:
          selectedsortOption.order === "none"
            ? ""
            : selectedSortBy || "last_activity",
        sort_direction:
          selectedsortOption.order === "none"
            ? ""
            : ["ASC", "DESC"].includes(selectedsortOption.order)
              ? selectedsortOption.order
              : "DESC",
      }),
    );
  }, [
    currentPage,
    itemsPerPage,
    selectedSearch,
    selectedSortBy,
    selectedsortOption.order,
    dispatch,
  ]);

  useEffect(() => {
    if (candidate?.total !== undefined && itemsPerPage) {
      const maxPage = Math.ceil(candidate.total / itemsPerPage) || 1;
      if (currentPage > maxPage) {
        setCurrentPage(maxPage);
      }
    }
  }, [candidate?.total, itemsPerPage, currentPage]);

  useEffect(() => {
    if (!selectedSortColumn || !selectedSortOrder) {
      return;
    }

    const normalizedOrder = ["ASC", "DESC"].includes(selectedSortOrder)
      ? selectedSortOrder
      : "DESC";

    setSelectedsortOption((prev) => {
      if (
        prev.column === selectedSortColumn &&
        prev.order === normalizedOrder
      ) {
        return prev;
      }

      return {
        ...prev,
        column: selectedSortColumn,
        order: normalizedOrder,
      };
    });
  }, [selectedSortColumn, selectedSortOrder]);

  const handleRowsPerPageChange = (newLimit) => {
    const cappedLimit = Math.min(newLimit, 100);
    setItemsPerPage(cappedLimit);
    setCurrentPage(1);
    localStorage.setItem("assessmentsRowsPerPage", cappedLimit);
  };

  const handleRowSelect = (row) => {
    history.push(`${PathName.CANDIDATES}/${row.id}`);
  };

  return (
    <div className="dashboard-page" style={{ overflow: "hidden" }}>
      <div className="page-header">
        <span className="headline-3 dark-100">{"Candidates"}</span>
      </div>
      <div
        className={`${daysLeft < 6 && auth?.data?.tenant_status === "trialing" && !openLeftDaysDisclaimer && ""} page-content`}
      >
        {candidate?.loading && (
          <div
            style={{
              position: "fixed",
              top: "0",
              bottom: "0",
              left: "0",
              right: "0",
              height: "25px",
              width: "25px",
              margin: "auto",
            }}
          >
            <div
              className="processing-indicator"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        )}
        <div
          className="w-100 hide-scrollbar"
          style={{
            height: "calc(100vh - 135px)",
            borderRadius: "24px",
            background: "white",
            overflowY: "auto",
          }}
        >
          {/* <div className='assessments-search-bar mt-3 mb-3'>
							<div className='d-flex' style={{ gap: '0.5em' }}>
								<div className='search'>
									<Search height={'24px'} width={'24px'} />
									<Input
										inputStyle={{
											background: 'transparent',
											border: 'none',
										}}
										className={'w-100'}
										placeholder={'Search by name...'}
										onChange={(e) => {
											setCustomSearch(e);
										}}
									/>
								</div>
							</div>
						</div> */}

          {candidate?.loading || loading ? (
            <div className="processing-indicator-wrapper medium-indicator w-100 h-100 d-flex align-items-center justify-content-center">
              <div
                className="processing-indicator primary"
                style={{ width: "40px", height: "40px" }}
              />
            </div>
          ) : testData === null || !testData?.length ? (
            <div className="default-assessment-card-container">
              <div
                className="default-assessment-text"
                style={{ marginTop: "24px" }}
              >
                <span className="body-1 dark-100">
                  Invite candidates for assessments to start seeing them here.
                </span>
              </div>
            </div>
          ) : (
            <Table
              headers={candidateTableHeader}
              setHighlightActiveRow={setHighlightActiveRow}
              data={testData}
              itemsPerPage={itemsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              onSelectRow={handleRowSelect}
              searchColumns={searchColumns}
              customSearch={customSearch}
              customSorting={customSorting}
              nullTableTitle={"No candidates to show"}
              nullTableDescription={
                "Candidates who have attempted your assessments appear here."
              }
              backendFilter={true}
              setSelectedsortOption={setSelectedsortOption}
              selectedsortOption={selectedsortOption}
              currentPage={currentPage}
              totalItems={candidate?.total || 0}
              totalPages={
                Math.ceil((candidate?.total || 0) / itemsPerPage) || 1
              }
              nextSlide={() => setCurrentPage((p) => p + 1)}
              prevSlide={() => setCurrentPage((p) => Math.max(1, p - 1))}
            />
          )}
        </div>
      </div>
    </div>
  );
}
